import { Fragment, useCallback, useEffect, useState, } from 'react'
import { styled } from '@mui/material'

import { useTranslation } from 'src/utils/i18n'
import { getLocalAndroidAll } from 'src/@api/dashboard'
import { setBlock, androidLocalRefresh } from 'src/@api/android'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'
import ContentLoading from 'src/components/ContentLoading'

// 自定义隐藏滚动条且保留滚动特性(div
const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

// 自定义滚动条(ul
const ScrollUlShow = styled('ul')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'thin',  /* 适用于 Firefox，设置滚动条为细 */
  '&::-webkit-scrollbar': {
    width: '4px',  /* 设置滚动条的宽度为 4px */
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',  /* 设置滚动条滑块的颜色 */
    borderRadius: '10px',  /* 设置滑块圆角 */
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',  /* 设置滚动条轨道的颜色 */
  },
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const BlockWifi = () => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const [showData, setShowData] = useState([]) // 展示的数据
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  // 分页参数
  const [pagination, setPagination] = useState({
    reqNum: 50, // 单次查询数量
    nowOffset: 0, // 偏移量
    maxNum: false // 全部数据查询完毕
  })

  const loadBlockState = useCallback(async deviceUdid => {
    const result = await setBlock({ device_id: deviceUdid })

    if (!result || !result.data || !result.data.ssid) {
      console.error('API response is invalid:', result)

      return []
    }

    return result.data.ssid
  }, [])

  //获取数据
  const getWifiData = useCallback(async (nowOffset, deviceUdid, { isCurrent } = {}) => {
    const response = await getLocalAndroidAll({
      username: readUserEmail(),
      params: {
        category: 'WIFI',
        deviceUdid,
        offset: nowOffset,
        limit: pagination.reqNum
      }
    })

    if (isCurrent && !isCurrent()) return

    if (response.data.contents === null) {
      setPagination(page => ({ ...page, maxNum: true }))
      setIsLoading(false)

      return
    }

    const result = [...new Map(response.data.contents.map(item => [item.name, item])).values()]
    const blockList = await loadBlockState(deviceUdid)

    if (isCurrent && !isCurrent()) return

    setShowData(result.map(item => ({ ...item, active: blockList.includes(item.name) })))
    setIsLoading(false)
  }, [loadBlockState, pagination.reqNum])

  const isBlock = async ({ name, action }) => {
    const deviceUdid = readLocalDeviceUdid()
    const result = await setBlock({
      device_id: deviceUdid,
      type: 'ssid',
      name,
      action: action ? true : false
    })
    await androidLocalRefresh({
      device_id: deviceUdid,
      type: 52,
      limit: action ? true : false,
      bundleid: name
    })
    const newBlockData = result.data.ssid
    setShowData(prevApps =>
      prevApps.map(app => ({
        ...app,
        active: newBlockData.includes(app.name) ? true : false
      }))
    )
  }

  // 设备切换时重置 Wi-Fi 列表并重新拉数
  useDeviceScopedInit(
    useCallback(
      (_, { context, isCurrent }) => {
        const deviceUdid = readLocalDeviceUdid(context)
        if (!deviceUdid) return

        setShowData([])
        setIsLoading(true)
        setPagination({ reqNum: 50, nowOffset: 0, maxNum: false })
        getWifiData(0, deviceUdid, { isCurrent })
      },
      [getWifiData]
    )
  )

  useEffect(() => {
    const title = t('Block Wi-Fi')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 埋点上报
  useEffect(() => {
    if (showData.length === 0) return
    console.log('showData updated:', showData)
    const events = showData
      .filter(item => item?.active === true)
      .map(item => ({
        event_name: 'Click_Block',
        properties: {
          wifi: item?.name || '',
        }
      }))
    if (events.length > 0) {
    }
  }, [showData])

  return (
    <Fragment>
      {/* 展示页 */}
      <ScrollDiv className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] overflow-auto bg-[#ffffff] relative'>
        <ScrollUlShow className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] overflow-auto smjs:pl-[66px] smjs:pr-[30px] smj:pl-[21px]'>
          <li className='grid grid-cols-2 bg-[#FFF] sticky top-0 text-[16px] text-[#000000] font-[Poppins] font-[600] leading-[24px] break-words p-[30px_80px_23px_0px] [border-bottom:1px_solid_#DCDCDC] smj:p-[23px_15px_23px_0px] relative'>
            <p className='text-left'>{t('Wi-fi Name')}</p>
            <p className='text-right'>{t('Filter by date')}</p>
          </li>
          {
            showData.map((item, index) => (
              <li key={index} className={`grid grid-cols-2 smj:flex smj:justify-between items-center text-[16px] text-[#222222] font-[Poppins] font-[400] leading-[24px] break-all
                                [border-bottom:1px_solid_#DCDCDC] smjs:p-[21px_90px_21px_0px] smj:p-[15px_23px_15px_0px]`}>

                {/* pc端样式 */}
                <p className='text-left smj:hidden'>{item.name}</p>

                {/* mb端样式 */}
                <div className='max-w-[60%] flex flex-col justify-center items-start smjs:hidden'>
                  <p className='text-[16px] text-[#000000] font-[Poppins] font-[600] leading-[24px] mb-[6px] break-words'>{item.name}</p>
                </div>

                <div className='flex justify-end select-none'>
                  <button
                    className={`w-max text-[12px] text-[#000000] font-[Poppins] font-[400] leading-[18px] p-[6px_23px]  ${!item.active ? 'bg-[#FFCF55]' : 'bg-[#E5E6EA]'} rounded-[15px]`}
                    onClick={() => { isBlock({ name: item.name, action: !item.active }) }}
                  >{!item.active ? t('Block') : t('Unblock')}</button>
                </div>
              </li>
            ))
          }
        </ScrollUlShow>
        {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(showData.length > 0 || isLoading) ||
          <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
            {t('It appears theres no data to show here yet')}<br />
            {t('If data exists it may take up to 1 hour to download and appear')}<br />
            {t('We suggest checking back in a little while')}
          </div>}
      </ScrollDiv>
    </Fragment>
  )
}

export default BlockWifi


